import { Form, Formik } from "formik"
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DocumentsForm from "./Documents/documentsForm";
import { useAuth } from "../../hooks/useAuth";
import {
  addBasicTraining,
  addDocumentsFormData,
  addFooter,
  addGoogleDriveFormData,
  addIntroAndSuggestedQuestionsFormData,
  // addIntroMessageFormData,
  addJournaling,
  addPremiumTraining,
  addSaveButton,
  addSocialMediaFormData,
  // addSuggestedQuestionsFormData,
  addTwitterFormData,
  addVoiceSampleFormData,
  addWeblinksFormData,
  addYoutubeFormData,
  addRoleSelection,
  addProductsFormData,
  newFormPartsType
} from "./helper";
import { USER_TYPE } from "../../config/const";
import { get_google_drive_links } from "../../utils/helper";
import { genericFormProps, IMMUTABLE_USER_KEYS, UserFormType } from "./signup.types";
import { toast } from "react-toastify";
import { Box, Button, ClickAwayListener, Dialog, Typography, Fade } from "@mui/material";
import * as Yup from 'yup';
import SaveButton from "./saveButton";
import GoogleDriveForm from "./Documents/googleDriveForm";
import WebLinksForm from "./WebLinks/webLinksForm";
import SocialMediaForm from "./SocialMedia/socialMediaForm";
import TwitterForm from "./SocialMedia/twitterForm";
import YoutubeLinksForm from "./SocialMedia/youtubeLinksForm";
import VoiceUploadForm from "./VoiceUpload/voiceUploadForm";
import Journaling from "../journaling";
import { BasicTraining } from "./onboardingOnly/basicTraining";
import { PremiumTraining } from "./onboardingOnly/premiumTraining";
import { Footer } from "./onboardingOnly/stepOneFooter";
import { Timestamp } from "firebase/firestore";
import { handleGoogleDriveResync, sendNewCreatorFromContestEmail, submitOnboardingFeedback } from "../../utils/api";
import { FirebaseError } from "firebase/app";
import { COMMON_FIREBASE_ERROR_CODE } from "../../config/errors";
import IntroductionForm from "./Introduction/introductionForm";
import SuggestedQuestionsForm from "./Introduction/suggestedQuestionsForm";
import LoadingPage from "../LoadingPage";
import RoleSelectionForm from "./RoleSelection/roleSelectionForm";
import StepCounter from "./StepCounter";
import ProductsForm from "./products/productForm";

const enableDocRequired = process.env.REACT_APP_ENABLE_DOC_REQUIRED === 'true';

const FormBuilder: React.FC = () => {
  const [formParts, setFormParts] = useState<newFormPartsType>({
    validationSchema: null,
    components: [],
    showSteps: false,
  });
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
  const [loading, setIsLoading] = useState<boolean>(false);
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const justOpened = useRef<boolean>(false);
  const initialValues = useRef<any>(null);
  const tempStorage = useRef<any>(null);
  const formRef: any = useRef(null);
  const handleLeave = useRef<() => void>(null);

  const location = useLocation();
  const {
    authUser,
    authLoading,
    token,
    fetchAndSetAuthUserData,
    updateUserProfileData
  } = useAuth();
  const params = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    setFormSubmitting(true)
    fetchAndSetAuthUserData(authUser.uid, token, true)
      .then(() => {
        setFormSubmitting(false)
      })
  }, []) //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!authLoading) {
      setInitialUserData(authUser);
    }
  }, [authUser, authLoading]);

  useEffect(() => {
    setIsLoading(true);
    setupForm(location)
      .finally(() => {
        setIsLoading(false);
      })
  }, [location]) //eslint-disable-line react-hooks/exhaustive-deps

  const setupForm = async (location: any) => {
    let newFormParts = {
      validationSchema: {},
      components: [],
      showSteps: false,
    };

    if (location.pathname.includes('/update/')) {
      if (!authUser?.index) {
        // redirect user to creator onboarding if they dont have an index set up
        navigate('/creator-onboarding')
        return;
      }
      switch (params?.formType) {
        case ('documents'):
          addSaveButton(newFormParts);
          addDocumentsFormData(newFormParts);
          addGoogleDriveFormData(newFormParts);
          break;
        case ('weblinks'):
          addSaveButton(newFormParts);
          addWeblinksFormData(newFormParts);
          break;
        case ('social'):
          addSaveButton(newFormParts);
          addSocialMediaFormData(newFormParts);
          addTwitterFormData(newFormParts);
          addYoutubeFormData(newFormParts);
          break;
        case ('voice'):
          addSaveButton(newFormParts);
          addVoiceSampleFormData(newFormParts);
          break;
        case ('introduction'):
          addIntroAndSuggestedQuestionsFormData(newFormParts);
          addSaveButton(newFormParts);
          break;
        case ('personality'):
          break;
        case ('journaling'):
          addJournaling(newFormParts);
          break;
        case ('products'):
          addSaveButton(newFormParts);
          addProductsFormData(newFormParts);
          break;
        default:
          // redirect if route not found?
          navigate(authUser ? `/${authUser.userName}` : '/')
          break;
      }
    } else if (location.pathname.includes('/creator-onboarding')) {
      newFormParts.showSteps = true;
      addRoleSelection(newFormParts);
      // addBasicTraining(newFormParts);
      addDocumentsFormData(newFormParts);
      addWeblinksFormData(newFormParts);
      // addPremiumTraining(newFormParts);
      addProductsFormData(newFormParts); // Products step after weblinks
      addYoutubeFormData(newFormParts);  // Youtube step after products
      addGoogleDriveFormData(newFormParts);
      addVoiceSampleFormData(newFormParts);
      addIntroAndSuggestedQuestionsFormData(newFormParts); // Add intro message and suggested questions
      // addFooter(newFormParts);
    }
    setFormParts(newFormParts)
  }

  const setInitialUserData = async (userData: any) => {
    // add disabled key for existing user web links
    if (userData?.userWebPage?.length >= 1) {
      userData.userWebPage = userData?.userWebPage?.map((item: any) => ({
        ...item,
        disabled: true,
      }));
    }
    if (userData?.scrape_webpage?.length >= 1) {
      userData.scrape_webpage = userData.scrape_webpage.map((item: any) => ({
        ...item,
        disabled: true,
      }));
    }

    const googleDriveLinks = get_google_drive_links(userData);

    let values = {
      documentId: userData?.documentId || '',
      fullName: userData?.fullName || '',
      userName: userData?.userName || '',
      shortBio: userData?.shortBio || '',
      email: userData?.email || '',
      twitterURL: userData?.twitterURL || '',
      linkedInURL: userData?.linkedInURL || '',
      instagramURL: userData?.instagramURL || '',
      youtubeURL: userData?.youtubeURL || '',
      tiktokURL: userData?.tiktokURL || '',
      userWebPage:
        userData?.userWebPage?.length >= 1
          ? userData?.userWebPage
          : [{ web_link: '', disabled: false }],
      scrape_webpage:
        userData?.scrape_webpage?.length >= 1
          ? userData.scrape_webpage
            .filter(item => item.web_link?.trim() !== '')
            .map(item => ({ ...item, disabled: true }))
          : [{ web_link: '', disabled: false }],
      youtube_links:
        userData?.youtube_links?.length >= 1
          ? userData?.youtube_links
          : [],
      google_drive_links: googleDriveLinks,
      anythingElse: userData?.anythingElse || '',
      profilePhoto: userData?.profilePicUrl || '',
      bannerPhoto: userData?.bannerPhotoUrl || '',
      userType: USER_TYPE.CREATOR,
      index: userData?.index || '',
      age: userData?.age || '',
      occupation: userData?.occupation || '',
      gender: userData?.gender || '',
      birthPlace: userData?.birthPlace || '',
      residencePlace: userData?.residencePlace || '',
      usagePurpose: userData?.usagePurpose || [],
      existingProfilePhoto: userData?.profilePhoto || '',
      existingProfilePicUrl: userData?.profilePicUrl || '',
      userDocument: [],
      existingUserDocument: userData?.userDocument || [],
      deletedUserDocument: [],
      // parse the file path in voiceSample to get the file name
      voiceSample: null,
      existingVoiceSample: userData?.voiceSample || '',
      existingBannerPhoto: userData?.bannerPhoto || '',
      existingBannerUrl: userData?.bannerPhotoUrl || '',
      // Product links
      productLinks: userData?.productLinks || [],
    }
    if (userData?.introMessage) {
      if (typeof userData?.introMessage === 'string') {
        values['introMessage'] = { message: userData?.introMessage, chips: ['', '', ''] };
      } else {
        values['introMessage'] = userData?.introMessage;
      }
    }
    initialValues.current = (values);
    if (formRef?.current) {
      formRef.current.resetForm({ values });
    }
  };

  const handleSubmit = async (values: UserFormType) => {
  let userFormData = { ...values };

  // Prevent mutation of specific keys
  for (let key of IMMUTABLE_USER_KEYS) {
    delete userFormData[key];
  }

  // Proceed with the rest of the validation and submission
  try {
    await validateLinkAndDocument(userFormData);
  } catch (error) {
    return;
  }

  setFormSubmitting(true);

if (values.usagePurpose && values.usagePurpose.length > 0) {
  submitOnboardingFeedback(values.usagePurpose)
}


  if (params?.formType !== 'personality') {
    const googleDriveLinks = (userFormData.google_drive_links || []).map((link: any) => ({
      url: link.url,
    }));

    userFormData.google_drive_links = googleDriveLinks;

    const extractFolderId = (url: string): string | null => {
      if (!url) return null;
      const match = url.match(/\/folders\/([a-zA-Z0-9_-]+)/);
      return match ? match[1] : url; 
    };

    const google_drive_folder_links = userFormData.google_drive_links
      .map((link) => link.url)
      .filter(Boolean);
    
    const google_drive_folder_ids = google_drive_folder_links
      .map(url => extractFolderId(url))
      .filter(Boolean);

     if (google_drive_folder_ids.length > 0) {
      handleGoogleDriveResync(authUser?.uid, google_drive_folder_ids)
        .then((res) => {
          if (res && res.userDocument) {
            console.log('Google Drive resync completed:', res);
          }
        })
        .catch((error) => {
          console.error('Google Drive resync error:', error);
        });
      }

    await updateUserData(userFormData);

    if (handleLeave.current) {
      handleLeave.current();
    } else {
      navigate(`/${values?.userName}`);
    }
    setFormSubmitting(false);
    toast.success('Processing your data... Please avoid submitting new requests until this completes. Tip: Upload multiple files at once for better efficiency.', { autoClose: 5000 });
  }
};

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (formSubmitting) {
      // Prevent click away if form is submitting
      event.preventDefault();
      return;
    }

     const target = event.target as HTMLElement;
      if (target.closest('.Toastify')) {
        return;
      }

    // If users click away while there are unsaved changes, we can prompt them
    if (formRef?.current && formRef.current.dirty) {
      // Stop the original event
      event.stopPropagation();

      // Store the original event details
      const originalTarget = event.target;
      const originalEvent = event;

      const handleConfirmLeave = () => {
        let newEvent;

        if (event.type === 'click' || event instanceof MouseEvent) {
          let origEvent = originalEvent as MouseEvent;
          // Create a new MouseEvent
          newEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
            button: origEvent.button,
            buttons: origEvent.buttons,
            clientX: origEvent.clientX,
            clientY: origEvent.clientY,
            ctrlKey: origEvent.ctrlKey,
            shiftKey: origEvent.shiftKey,
            altKey: origEvent.altKey,
            metaKey: origEvent.metaKey
          });
        } else if (event.type === 'touchstart' || event instanceof TouchEvent) {
          let origEvent = originalEvent as TouchEvent;
          let clientX, clientY;
          if (event.changedTouches.length > 0) {
            clientX = event.changedTouches[0].clientX;
            clientY = event.changedTouches[0].clientY;
          } else if (event.touches.length > 0) {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
          }
          // Create a new MouseEvent
          newEvent = new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
            button: 0,
            buttons: 1,
            clientX: clientX,
            clientY: clientY,
            ctrlKey: origEvent.ctrlKey,
            shiftKey: origEvent.shiftKey,
            altKey: origEvent.altKey,
            metaKey: origEvent.metaKey
          });
        }

        // Temporarily remove this handler to avoid recursion
        originalTarget.removeEventListener(event.type, handleClickAway);

        // Dispatch the new event
        if (newEvent) {
          originalTarget.dispatchEvent(newEvent);
        }

        // Re-add the handler if needed
        originalTarget.addEventListener(event.type, handleClickAway);

        // Reset the form to its initial values
        if (formRef?.current) {
          formRef.current.resetForm({ values: initialValues.current });
          setFormSubmitting(false);
        }

        // Close the confirmation modal
        setConfirmationModal(false)

        // Remove reference to the handler
        handleLeave.current = null;
      }

      // Store the handler to be used later
      handleLeave.current = handleConfirmLeave;

      // Show confirmation popover
      justOpened.current = true;
      setConfirmationModal(true);
      setTimeout(() => {
        justOpened.current = false;
      }, 500);
    }
  }

  const updateUserData = async (values: UserFormType) => {
    updateUserProfileData(values, values?.index ? 'update' : 'create')
      .then(() => {
        // if (!skipRedirect) navigate(`/${values?.userName}`);
        // setFormSubmitting(false);
        // if (!skipRedirect) toast.success('Data processing, this may take a few moments...');
        if (values && !values.index) {
          sendNewCreatorFromContestEmail(authUser?.documentId)
        } // Send email
      })
      .catch((error) => {
        setFormSubmitting(false);
        const firebaseError = (error as FirebaseError)?.code;
        toast.error(
          firebaseError && COMMON_FIREBASE_ERROR_CODE[firebaseError]
            ? COMMON_FIREBASE_ERROR_CODE[firebaseError]
            : 'There is some error with the user updating. Please try again'
        );
      });
  };

  const validateLinkAndDocument = async (userFormData: UserFormType) => {
    //check web links data remove delete key and remove empty links
    return new Promise<void>((resolve, reject) => {
      if (userFormData?.userWebPage?.length >= 0) {
        userFormData.userWebPage = userFormData?.userWebPage?.filter((link) => {
          delete link?.disabled;
          return link?.web_link?.trim().length > 0;
        });
      }

      if (userFormData?.scrape_webpage?.length) {
        userFormData.scrape_webpage = userFormData?.scrape_webpage?.filter((link) => {
          delete link?.disabled;
          return link?.web_link?.trim().length > 0;
        });
      }
  
      resolve();
    });
  };

  const handleCloseModal = () => {
    if (justOpened.current) return;
    setConfirmationModal(false);
    handleLeave.current = null;
  }

  // Check if role selection step is valid (both occupation and usagePurpose are filled)
  const isRoleSelectionValid = (values: any): boolean => {
    const hasOccupation = values?.occupation && values.occupation.trim() !== '';
    const hasUsagePurpose = values?.usagePurpose && Array.isArray(values.usagePurpose) && values.usagePurpose.length > 0;
    return hasOccupation && hasUsagePurpose;
  };

  const handleNextStep = async () => {
    if (currentStep < formParts.components.length - 1) {
      // Get the current component type
      const currentComponent = formParts.components[currentStep];

      // Validate role selection step
      if (currentComponent === 'roleSelection') {
        if (formRef?.current) {
          const values = formRef.current.values;
          if (!isRoleSelectionValid(values)) {
            toast.error('Please select what best describes you and at least one purpose to continue');
            return;
          }
        }
      }

      if (currentComponent === 'weblinks') {
          if (formRef?.current) {
              const values = formRef.current.values;

              const hasWebLinks =
                  values.userWebPage.some(
                    (link: { web_link: string }) => {
                      return link?.web_link?.trim().length > 0
                    }
                  );

              const hasSitemaps =
                  values.scrape_webpage.some(
                    (link: { web_link: string }) => {
                      return link?.web_link?.trim().length > 0
                    }
                  );

              const hasDocuments =
                  values.userDocument.length > 0 ||
                  values.existingUserDocument.length > 0;

              if (!hasWebLinks && !hasSitemaps && !hasDocuments) {
                  toast.error(
                      'Please add at least one weblink, sitemap, or document to continue'
                  );
                  return;
              }
          }
       }

      // If current step is documents or weblinks, validate document/link requirements
      if (currentComponent === 'documents' ) {
        if (formRef?.current) {
          try {
            const userFormData = { ...formRef.current.values };
            await validateLinkAndDocument(userFormData);
          } catch (error) {
            // Validation failed, don't proceed to next step
            return;
          }
        }
      }

      setCurrentStep(currentStep + 1);
    }
  }

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }

  // Check if Continue button should be disabled based on current step
  const isContinueDisabled = (values: any): boolean => {
    if (formSubmitting) return true;
    
    const currentComponent = formParts.components[currentStep];
    
    if (currentComponent === 'roleSelection') {
      return !isRoleSelectionValid(values);
    }
    
    return false;
  };

  const renderFormComponent = (component: string, props: genericFormProps, index?: number) => {
    const key = index !== undefined ? index : component;
    switch (component) {
      case ('roleSelection'):
        return <RoleSelectionForm key={key} {...props} formSubmitting={formSubmitting} showSteps={formParts.showSteps} />
      case ('documents'):
        return <DocumentsForm key={key} {...props} formSubmitting={formSubmitting} showSteps={formParts.showSteps} />
      case ('saveButton'):
        return <SaveButton
          key={component}
          {...props}
          formSubmitting={formSubmitting}
          validationSchema={Yup.object(formParts.validationSchema)}
        />
      case ('googleDrive'):
        return <GoogleDriveForm key={key} {...props} formSubmitting={formSubmitting} showSteps={formParts.showSteps} />
      case ('weblinks'):
        return <WebLinksForm key={key} {...props} formSubmitting={formSubmitting} showSteps={formParts.showSteps} />
      case ('socialMedia'):
        return <SocialMediaForm key={key} {...props} formSubmitting={formSubmitting} showSteps={formParts.showSteps} />
      case ('twitter'):
        return <TwitterForm key={key} {...props} formSubmitting={formSubmitting} showSteps={formParts.showSteps} />
      case ('youtube'):
        return <YoutubeLinksForm key={key} {...props} formSubmitting={formSubmitting} showSteps={formParts.showSteps} />
      case ('voiceSample'):
        return <VoiceUploadForm key={key} {...props} formSubmitting={formSubmitting} showSteps={formParts.showSteps} />
      case ('journaling'):
        return <Journaling key={key} />
      case ('introMessage'):
        return <IntroductionForm key={key} {...props} formSubmitting={formSubmitting} showSteps={formParts.showSteps} tempStorage={tempStorage} />
      case ('suggestedQuestions'):
        return <SuggestedQuestionsForm key={key} {...props} formSubmitting={formSubmitting} showSteps={formParts.showSteps} tempStorage={tempStorage} />
      case ('basicTraining'):
        return <BasicTraining key={key} />
      case ('premiumTraining'):
        return <PremiumTraining key={key} />
      case ('footer'):
        return <Footer key={key} {...props} formSubmitting={formSubmitting} />
      case ('products'):
        return <ProductsForm key={key} {...props} formSubmitting={formSubmitting} showSteps={formParts.showSteps} />
      default:
        return null;
    }
  }

  return (
    (loading || !formParts.components.length) ?
      <LoadingPage /> :
      <ClickAwayListener
        mouseEvent='onMouseDown'
        touchEvent='onTouchStart'
        onClickAway={handleClickAway}
      >
        <Box>
          <Formik
            initialValues={initialValues.current}
            validationSchema={Yup.object(formParts.validationSchema)}
            onSubmit={handleSubmit}
            innerRef={formRef}
            enableReinitialize
          >
            {(props: genericFormProps) => (
              <Form
                id={'form-creator-onboarding'}
                autoComplete='off'
                noValidate
                style={{
                  width: '100%',
                }}
              >
                <Box
                  display='flex'
                  sx={{
                    minHeight: '100vh',
                    maxWidth: '1400px',
                    mx: 'auto',
                    flexDirection: { xs: 'column', md: 'row' },
                  }}
                >
                  {/* Mobile Step Counter - Top (shows only on xs screens) */}
                  {formParts.showSteps && (
                    <Box sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }}>
                      <StepCounter components={formParts.components} currentStep={currentStep} />
                    </Box>
                  )}

                  {/* Form Content */}
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      bgcolor: 'background.default',
                      borderRight: formParts.showSteps ? (theme) => `1px solid ${theme.palette.divider}` : 'none',
                    }}
                  >


                    {/* Form Content Area */}
                    <Box
                      sx={{
                        flex: 1,
                        p: { xs: 3, sm: 4, md: 6 },
                        overflow: 'auto',
                      }}
                    >
                      {formParts.showSteps ?
                        (
                          // Onboarding: Show single step with fade animation
                          <Fade in={true} timeout={400} key={currentStep}>
                            <Box>
                              {renderFormComponent(formParts.components[currentStep], props)}
                            </Box>
                          </Fade>
                        ) : (
                          // Update pages: Show all components stacked
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {formParts.components.map((component, index) => renderFormComponent(component, props, index))}
                          </Box>
                        )}
                    </Box>

                    {/* Bottom Navigation Bar - Only for creator-onboarding */}
                    {formParts.showSteps && (
                      <Box
                        sx={{
                          p: { xs: 2, sm: 3, md: 4 },
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          bgcolor: 'background.paper',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 2,
                        }}
                      >
                        <Button
                          variant="text"
                          onClick={handlePreviousStep}
                          disabled={currentStep === 0 || formSubmitting}
                          size="large"
                          sx={{
                            color: 'text.secondary',
                            textTransform: 'none',
                            fontSize: '1rem',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          Back
                        </Button>

                        {currentStep === formParts.components.length - 1 ? (
                          <Button
                            variant="contained"
                            onClick={() => handleSubmit(props.values)}
                            disabled={formSubmitting}
                            size="large"
                            sx={{
                              px: 4,
                              py: 1.5,
                              borderRadius: 1,
                              textTransform: 'none',
                              fontSize: '1rem',
                            }}
                          >
                            {formSubmitting ? 'Submitting...' : 'Complete'}
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={handleNextStep}
                            disabled={isContinueDisabled(props.values)}
                            size="large"
                            sx={{
                              px: 4,
                              py: 1.5,
                              borderRadius: 1,
                              textTransform: 'none',
                              fontSize: '1rem',
                            }}
                          >
                            Continue
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Desktop Step Counter - Right Side (shows only on md+ screens) */}
                  {formParts.showSteps && (
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                      <StepCounter components={formParts.components} currentStep={currentStep} />
                    </Box>
                  )}
                </Box>

                <Dialog
                  open={confirmationModal}
                  onClose={handleCloseModal}
                  disableRestoreFocus
                  disableAutoFocus
                  slotProps={{
                    paper: {
                      sx: {
                        maxWidth: '340px'
                      }
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                      p: 3,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        width: '100%',
                      }}
                    >
                      <Typography variant="h6" >
                        You have unsaved changes.
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        If you leave now, your changes won't be saved.
                        Save them first to make sure they take effect.
                      </Typography>
                    </Box>

                    <Box
                      display='flex'
                      justifyContent='space-between'
                      alignItems='center'
                      width='100%'
                    >
                      <Button
                        variant="contained"
                        size="large"
                        color="error"
                        onClick={() => {
                          if (handleLeave.current) {
                            handleLeave.current();
                          } else {
                            setConfirmationModal(false);
                          }
                        }}
                      >
                        Discard Changes
                      </Button>
                      <Button
                        variant="contained"
                        size='large'
                        onClick={() => {
                          handleSubmit(props.values);
                        }}
                        id='save-button'
                        sx={{
                          boxShadow: (theme) => `${theme.shadows[2]} !important`,
                        }}
                      >
                        Save
                      </Button>
                    </Box>
                  </Box>
                </Dialog>
              </Form>
            )}
          </Formik>
        </Box>
      </ClickAwayListener>
  )
}

export default FormBuilder;